import * as React from "react";
import { Formik, Form, Field } from "formik";
import mapValues from "lodash/mapValues";
import moment from "moment";
import styled, { css } from "styled-components";
import useProductName from "brand-customization/useProductName";
import { Trans, useTranslation } from "react-i18next";
import i18n from "i18n";
import NumberUtils from "util/NumberUtils";
import BackendTypes from "archive/logic/BackendTypes";
import { HelpBlock, Input } from "components/bootstrap";
import { FormikInput, FormSubmit, Section, Select } from "components/common";
import { defaultCompare as naturalSort } from "logic/DefaultCompare";
import useHistory from "routing/useHistory";
import useDataLakeConfigMutation from "data-lake/configurations/hooks/useDataLakeConfigMutation";
import DataLakeBackendWarning from "data-lake/data-lake-backend-config/DataLakeBackendWarning";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import DataLakeRoutes from "data-lake/Routes";
import { formatDuration, formatRetentionLabel, validateRetention } from "data-lake/logic/Utils";
import AppConfig from "util/AppConfig";
const isCloud = AppConfig.isCloud();
const defaultValues = {
  active_backend: null,
  iceberg_commit_interval: "PT1H",
  iceberg_target_file_size: 536870912,
  parquet_row_group_size: 134217728,
  parquet_page_size: 8192,
  journal_reader_batch_size: 500,
  optimize_job_enabled: true,
  optimize_job_interval: "PT1H",
  optimize_rewrite_manifests_enabled: true,
  optimize_delete_orphan_files_enabled: true,
  parallel_retrieval_enabled: true,
  retrieval_convert_threads: -1,
  retrieval_convert_batch_size: 1,
  retrieval_inflight_requests: 3,
  retrieval_bulk_batch_size: 2500,
  retention_time: "PT1D",
  size_metrics_collection_interval: "PT15M"
};
const StyledFormSubmit = styled(FormSubmit)(
  ({ theme }) => css`
    display: flex;
    justify-content: flex-end;
    margin-top: ${theme.spacings.md};
  `
);
const formatBytes = (value) => NumberUtils.formatBytes(value);
const isValidDuration = (value) => moment.duration(value).asMilliseconds() >= 36e3;
const validateDuration = (value) => !isValidDuration(value) && i18n.t("config_form.invalid_duration", { ns: "enterprise_data_lake" });
const formatBackendValidationErrors = (backendErrors) => {
  const backendErrorStrings = mapValues(backendErrors, (errorArray) => `${errorArray.join(" ")}`);
  return { ...backendErrorStrings };
};
const getBackendOptions = (backends) => backends.map((backend) => ({
  value: backend.id,
  label: `${backend.title} (${BackendTypes.getBackendType(backend.settings.type).label})`
})).sort((a, b) => naturalSort(a.label.toLowerCase(), b.label.toLowerCase()));
const DataLakeConfigForm = ({ config, backends, showActiveBackend }) => {
  const { t } = useTranslation("enterprise_data_lake");
  const productName = useProductName();
  const { onUpdateDataLakeConfig } = useDataLakeConfigMutation();
  const history = useHistory();
  const sendTelemetry = useSendTelemetry();
  const sortedBackends = getBackendOptions(backends);
  const redirectToDataLake = () => history.push(DataLakeRoutes.ARCHIVE.LIST);
  const handleSubmit = (values, { setErrors }) => onUpdateDataLakeConfig({
    config: {
      ...values,
      retention_time: values.retention_time === "0" ? null : values.retention_time
    }
  }).then(() => {
    redirectToDataLake();
  }).catch((error) => {
    var _a, _b;
    if (typeof ((_b = (_a = error == null ? void 0 : error.additional) == null ? void 0 : _a.body) == null ? void 0 : _b.errors) === "object") {
      setErrors(formatBackendValidationErrors(error.additional.body.errors));
    }
  }).finally(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.CONFIGURATION_SAVED, {
      app_pathname: "data-lake"
    });
  });
  return /* @__PURE__ */ React.createElement(Formik, { initialValues: config || defaultValues, onSubmit: handleSubmit }, ({ values, isSubmitting, setFieldValue, initialValues, isValid }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement("fieldset", { disabled: isCloud }, /* @__PURE__ */ React.createElement(Section, { title: t("config_form.section_configuration") }, showActiveBackend && /* @__PURE__ */ React.createElement(Field, { name: "active_backend" }, ({ field: { name, value }, form: { touched, setFieldTouched } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      help: t("config_form.active_backend_help"),
      id: "active-backend",
      label: t("config_form.active_backend_label")
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        inputId: "active-backend",
        name: "active_backend",
        placeholder: t("config_form.select_active_backend"),
        "aria-label": t("config_form.select_active_backend"),
        options: sortedBackends,
        required: true,
        value,
        disabled: isCloud,
        onChange: (selected) => {
          setFieldTouched(name, true);
          setFieldValue(name, selected);
        }
      }
    ),
    /* @__PURE__ */ React.createElement(HelpBlock, null),
    touched[name] && value !== initialValues[name] && /* @__PURE__ */ React.createElement(DataLakeBackendWarning, null)
  )), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "text",
      name: "iceberg_commit_interval",
      id: "iceberg-commit-interval",
      label: t("config_form.commit_interval_label"),
      help: /* @__PURE__ */ React.createElement(
        Trans,
        {
          t,
          i18nKey: "config_form.commit_interval_help",
          values: { productName },
          components: { em: /* @__PURE__ */ React.createElement("em", null) }
        }
      ),
      addonAfter: formatDuration(values.iceberg_commit_interval),
      validate: validateDuration,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      name: "iceberg_target_file_size",
      id: "iceberg-target-file-size",
      label: t("config_form.target_file_size_label"),
      help: /* @__PURE__ */ React.createElement(
        Trans,
        {
          t,
          i18nKey: "config_form.target_file_size_help",
          values: { productName },
          components: { em: /* @__PURE__ */ React.createElement("em", null) }
        }
      ),
      addonAfter: formatBytes(values.iceberg_target_file_size),
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      name: "parquet_row_group_size",
      id: "parquet-row-group-size",
      label: t("config_form.row_group_size_label"),
      help: /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "config_form.row_group_size_help", components: { em: /* @__PURE__ */ React.createElement("em", null) } }),
      addonAfter: formatBytes(values.parquet_row_group_size),
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      name: "parquet_page_size",
      id: "parquet-page-size",
      label: t("config_form.page_size_label"),
      help: /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "config_form.page_size_help", components: { em: /* @__PURE__ */ React.createElement("em", null) } }),
      addonAfter: formatBytes(values.parquet_page_size),
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      name: "journal_reader_batch_size",
      id: "journal-reader-batch-size",
      label: t("config_form.journal_reader_batch_size_label"),
      help: /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "config_form.journal_reader_batch_size_help", components: { em: /* @__PURE__ */ React.createElement("em", null) } }),
      required: true
    }
  )), /* @__PURE__ */ React.createElement(Section, { title: t("config_form.section_optimization") }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "optimize_job_enabled",
      id: "optimize-job-enabled",
      label: t("config_form.enable_optimization_label"),
      help: t("config_form.enable_optimization_help")
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "optimize_rewrite_manifests_enabled",
      id: "optimize-rewrite-manifests-enabled",
      label: t("config_form.enable_manifest_rewrite_label"),
      disabled: !values.optimize_job_enabled,
      help: t("config_form.enable_manifest_rewrite_help", { productName })
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "optimize_delete_orphan_files_enabled",
      id: "optimize-delete-orphan-files-enabled",
      label: t("config_form.enable_orphan_deletion_label"),
      disabled: !values.optimize_job_enabled,
      help: t("config_form.enable_orphan_deletion_help", { productName })
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "text",
      name: "optimize_job_interval",
      id: "optimize-job-interval",
      label: t("config_form.optimize_interval_label"),
      disabled: !values.optimize_job_enabled,
      help: t("config_form.optimize_interval_help"),
      addonAfter: formatDuration(values.optimize_job_interval),
      validate: validateDuration,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "text",
      name: "size_metrics_collection_interval",
      id: "size-metrics-collection-interval",
      label: t("config_form.size_metrics_interval_label"),
      help: t("config_form.size_metrics_interval_help"),
      addonAfter: formatDuration(values.size_metrics_collection_interval),
      validate: validateDuration,
      required: true
    }
  )), /* @__PURE__ */ React.createElement(Section, { title: t("config_form.section_retrieval") }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "parallel_retrieval_enabled",
      id: "parallel-retrieval-enabled",
      label: t("config_form.enable_parallel_retrieval_label"),
      help: t("config_form.enable_parallel_retrieval_help")
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      name: "retrieval_inflight_requests",
      id: "retrieval-inflight-requests",
      label: t("config_form.inflight_requests_label"),
      disabled: !values.parallel_retrieval_enabled,
      help: t("config_form.inflight_requests_help")
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      name: "retrieval_bulk_batch_size",
      id: "retrieval-bulk-batch-size",
      label: t("config_form.bulk_batch_size_label"),
      disabled: !values.parallel_retrieval_enabled,
      help: t("config_form.bulk_batch_size_help")
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      name: "retrieval_convert_threads",
      id: "retrieval-convert-threads",
      label: t("config_form.convert_threads_label"),
      disabled: !values.parallel_retrieval_enabled,
      help: t("config_form.convert_threads_help")
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      name: "retrieval_convert_batch_size",
      id: "retrieval-convert-batch-size",
      label: t("config_form.convert_batch_size_label"),
      disabled: !values.parallel_retrieval_enabled,
      help: t("config_form.convert_batch_size_help")
    }
  )), /* @__PURE__ */ React.createElement(Section, { title: t("config_form.section_retention") }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "text",
      name: "retention_time",
      id: "retention-time",
      label: t("config_form.retention_label"),
      help: t("config_form.retention_help"),
      addonAfter: formatRetentionLabel(values.retention_time),
      validate: validateRetention
    }
  ))), !isCloud && /* @__PURE__ */ React.createElement(
    StyledFormSubmit,
    {
      submitButtonText: t("config_form.submit_button"),
      submitLoadingText: t("config_form.submit_loading"),
      isSubmitting,
      disabledSubmit: !isValid,
      isAsyncSubmit: true,
      onCancel: redirectToDataLake
    }
  )));
};
export default DataLakeConfigForm;
